const DAY_MS = 24 * 60 * 60 * 1e3;
const HOUR_MS = 60 * 60 * 1e3;
const MINUTE_MS = 60 * 1e3;
const NEAR_END_MS = 2 * 60 * 1e3;
const NEAR_END_INTERVAL_MS = 1e3;
const DEFAULT_INTERVAL_MS = 3e4;
const timers = /* @__PURE__ */ new WeakMap();
const ctaClickHandlers = /* @__PURE__ */ new WeakMap();
const previousUnits = /* @__PURE__ */ new WeakMap();
export function remainingTimeParts(endsAtMs, nowMs = Date.now()) {
  const ms = Math.floor(endsAtMs - nowMs);
  if (ms <= 0) {
    return { unit: "expired", count: 0 };
  }
  if (ms >= DAY_MS) {
    return { unit: "days", count: Math.floor(ms / DAY_MS) };
  }
  if (ms >= HOUR_MS) {
    return { unit: "hours", count: Math.floor(ms / HOUR_MS) };
  }
  if (ms >= MINUTE_MS) {
    return { unit: "minutes", count: Math.floor(ms / MINUTE_MS) };
  }
  return { unit: "seconds", count: Math.floor(ms / 1e3) || 1 };
}
function pluralCategory(count, locale = typeof document !== "undefined" ? document.documentElement.lang : "pl") {
  const n = Math.abs(Number(count)) || 0;
  const normalizedLocale = String(locale || "pl").toLowerCase();
  if (normalizedLocale.startsWith("en")) {
    return n === 1 ? "one" : "other";
  }
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (n === 1) {
    return "one";
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return "few";
  }
  return "many";
}
function pluralize(translations, count) {
  if (!translations) {
    return "";
  }
  if (typeof translations === "string") {
    return translations;
  }
  const key = pluralCategory(count);
  return translations[key] || translations.other || translations.many || translations.few || translations.one || "";
}
function interpolate(template, values) {
  if (!template) {
    return "";
  }
  return template.replace(/%\{(\w+)\}/g, (_, key) => {
    return values[key] !== void 0 && values[key] !== null ? String(values[key]) : "";
  });
}
export function parseI18n(element) {
  const script = element.querySelector("script[type='application/json'][data-countdown-i18n]");
  const raw = script ? script.textContent : element.getAttribute("data-i18n");
  if (!raw || !raw.trim()) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw.trim());
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    return null;
  }
}
function hasUsableI18n(i18n, mode) {
  var _a, _b;
  if (!i18n || typeof i18n !== "object") {
    return false;
  }
  if (!i18n.expired) {
    return false;
  }
  if (mode === "card") {
    return Boolean(((_a = i18n.text) == null ? void 0 : _a.days) && ((_b = i18n.time) == null ? void 0 : _b.days));
  }
  return Boolean(i18n.days || i18n.hours || i18n.minutes || i18n.seconds);
}
function tickInterval(remainingMs) {
  return remainingMs < NEAR_END_MS ? NEAR_END_INTERVAL_MS : DEFAULT_INTERVAL_MS;
}
function preventCtaClick(event) {
  event.preventDefault();
  event.stopPropagation();
}
function disableCta(element) {
  if (!element || element.getAttribute("aria-disabled") === "true") {
    return;
  }
  element.setAttribute("aria-disabled", "true");
  element.setAttribute("tabindex", "-1");
  element.classList.add("pointer-events-none", "opacity-50");
  if (element.tagName === "BUTTON") {
    element.disabled = true;
  }
  const handler = preventCtaClick;
  element.addEventListener("click", handler);
  ctaClickHandlers.set(element, handler);
}
function findCtas(countdownElement) {
  const mode = countdownElement.dataset.countdownMode;
  if (mode === "card") {
    return [...countdownElement.querySelectorAll("[data-countdown-cta]")];
  }
  const offerRoot = countdownElement.closest(".offer") || document;
  return [...offerRoot.querySelectorAll("[data-countdown-cta]")];
}
function syncAriaLive(labelElement, parts) {
  if (!labelElement) {
    return;
  }
  labelElement.setAttribute("role", "timer");
  labelElement.setAttribute("aria-atomic", "true");
  const previousUnit = previousUnits.get(labelElement);
  previousUnits.set(labelElement, parts.unit);
  if (parts.unit === "expired") {
    labelElement.setAttribute("aria-live", "assertive");
    return;
  }
  if (parts.unit === "seconds") {
    labelElement.setAttribute("aria-live", previousUnit === "seconds" ? "off" : "polite");
    return;
  }
  labelElement.setAttribute("aria-live", "polite");
}
function renderCardLabel(labelElement, parts, i18n) {
  var _a, _b;
  syncAriaLive(labelElement, parts);
  if (parts.unit === "expired") {
    labelElement.innerHTML = "";
    const span = document.createElement("span");
    span.textContent = i18n.expired || "";
    labelElement.appendChild(span);
    return;
  }
  let textEl = labelElement.querySelector("[data-countdown-text]");
  let counterEl = labelElement.querySelector("[data-countdown-counter]");
  if (!textEl || !counterEl) {
    labelElement.innerHTML = "";
    textEl = document.createElement("span");
    textEl.className = "text-neutral-600";
    textEl.setAttribute("data-countdown-text", "");
    counterEl = document.createElement("span");
    counterEl.className = "offer-card__remaining-time-counter";
    counterEl.setAttribute("data-countdown-counter", "");
    labelElement.appendChild(textEl);
    labelElement.appendChild(counterEl);
  }
  textEl.textContent = pluralize((_a = i18n.text) == null ? void 0 : _a[parts.unit], parts.count);
  const unitTemplate = pluralize((_b = i18n.time) == null ? void 0 : _b[parts.unit], parts.count);
  counterEl.textContent = interpolate(unitTemplate, { count: parts.count });
}
function renderDetailsLabel(labelElement, parts, i18n) {
  syncAriaLive(labelElement, parts);
  if (parts.unit === "expired") {
    labelElement.textContent = i18n.expired || "";
    return;
  }
  const template = pluralize(i18n[parts.unit], parts.count);
  labelElement.textContent = interpolate(template, {
    count: parts.count,
    date: i18n.date
  });
}
function updateCountdown(element) {
  const endsAtMs = Date.parse(element.dataset.endsAt);
  if (Number.isNaN(endsAtMs)) {
    return 0;
  }
  const mode = element.dataset.countdownMode;
  const i18n = parseI18n(element);
  const parts = remainingTimeParts(endsAtMs);
  const remainingMs = endsAtMs - Date.now();
  if (!hasUsableI18n(i18n, mode)) {
    if (parts.unit === "expired") {
      findCtas(element).forEach(disableCta);
    }
    return remainingMs;
  }
  const labelElement = element.querySelector("[data-countdown-label]");
  if (labelElement) {
    if (mode === "card") {
      renderCardLabel(labelElement, parts, i18n);
    } else {
      renderDetailsLabel(labelElement, parts, i18n);
    }
  }
  if (parts.unit === "expired") {
    findCtas(element).forEach(disableCta);
  }
  return remainingMs;
}
function clearTimer(element) {
  const timerId = timers.get(element);
  if (timerId) {
    clearTimeout(timerId);
    timers.delete(element);
  }
}
function scheduleCountdown(element) {
  clearTimer(element);
  const remainingMs = updateCountdown(element);
  if (remainingMs <= 0) {
    return;
  }
  const timerId = setTimeout(() => {
    scheduleCountdown(element);
  }, tickInterval(remainingMs));
  timers.set(element, timerId);
}
export function initOfferCountdowns(root = document) {
  const elements = root.querySelectorAll ? root.querySelectorAll("[data-offer-countdown]") : [];
  elements.forEach((element) => {
    scheduleCountdown(element);
  });
}
export function destroyOfferCountdowns(root = document) {
  const elements = root.querySelectorAll ? root.querySelectorAll("[data-offer-countdown]") : [];
  elements.forEach((element) => {
    clearTimer(element);
  });
}
function boot() {
  initOfferCountdowns(document);
}
if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  document.addEventListener("ajax:loaded", () => {
    initOfferCountdowns(document);
  });
}
export default { remainingTimeParts, initOfferCountdowns, destroyOfferCountdowns, parseI18n };
